import {
  OUTPUT_DOCUMENT_CATEGORIES,
  type OutputDocumentCategory,
} from '../constants/output-document-categories';
import type { OutputCategoryFilterState } from '../types/output-category-filter-state';

export const selectAllOutputCategories = (): OutputCategoryFilterState => ({
  outputCategoryFilterMode: 'exclude',
  excludeOutputDocumentCategories: [],
});

export const clearOutputCategories = (): OutputCategoryFilterState => ({
  outputCategoryFilterMode: 'include',
  outputDocumentCategories: [],
});

/**
 * Narrows a wider search state down to the output-category filter union.
 */
export const getOutputCategoryFilterSlice = (
  source: OutputCategoryFilterState,
): OutputCategoryFilterState => {
  if (source.outputCategoryFilterMode === 'exclude') {
    return {
      outputCategoryFilterMode: 'exclude',
      excludeOutputDocumentCategories: source.excludeOutputDocumentCategories,
    };
  }

  return {
    outputCategoryFilterMode: 'include',
    outputDocumentCategories: source.outputDocumentCategories,
  };
};

const addUniqueId = (ids: string[], id: string) => {
  if (ids.includes(id)) return ids;
  return [...ids, id];
};

const removeId = (ids: string[], id: string) =>
  ids.filter((currentId) => currentId !== id);

export const getIsOutputCategorySelected = (
  filter: OutputCategoryFilterState,
  category: string,
) => {
  if (filter.outputCategoryFilterMode === 'exclude') {
    return !filter.excludeOutputDocumentCategories.includes(category);
  }

  return filter.outputDocumentCategories.includes(category);
};

export const getNextOutputCategoryFilter = (
  filter: OutputCategoryFilterState,
  category: string,
  checked: boolean,
): OutputCategoryFilterState => {
  if (filter.outputCategoryFilterMode === 'exclude') {
    return {
      outputCategoryFilterMode: 'exclude',
      excludeOutputDocumentCategories: checked
        ? removeId(filter.excludeOutputDocumentCategories, category)
        : addUniqueId(filter.excludeOutputDocumentCategories, category),
    };
  }

  return {
    outputCategoryFilterMode: 'include',
    outputDocumentCategories: checked
      ? addUniqueId(filter.outputDocumentCategories, category)
      : removeId(filter.outputDocumentCategories, category),
  };
};

/** Collapse include/exclude UI state to the selected category values. */
export const getCollapsedOutputCategories = (
  filter: OutputCategoryFilterState,
): OutputDocumentCategory[] => {
  if (filter.outputCategoryFilterMode === 'exclude') {
    if (filter.excludeOutputDocumentCategories.length === 0) {
      return [...OUTPUT_DOCUMENT_CATEGORIES];
    }

    return OUTPUT_DOCUMENT_CATEGORIES.filter(
      (category) => !filter.excludeOutputDocumentCategories.includes(category),
    );
  }

  return OUTPUT_DOCUMENT_CATEGORIES.filter((category) =>
    filter.outputDocumentCategories.includes(category),
  );
};

/**
 * Maps UI filter to API payload.
 * Sends the selected categories (including Select All / all 15).
 * Omits the field when none are selected; never sends an empty array.
 */
export const getOutputCategoryFilterSearchParams = (
  filter: OutputCategoryFilterState,
): { outputDocumentCategories?: OutputDocumentCategory[] } => {
  const selected = getCollapsedOutputCategories(filter);

  if (selected.length === 0) {
    return {};
  }

  return { outputDocumentCategories: selected };
};

export const getHasActiveOutputCategoryFilter = (
  filter: OutputCategoryFilterState,
) => getCollapsedOutputCategories(filter).length > 0;

export const getSelectedOutputCategoryCount = (
  filter: OutputCategoryFilterState,
) => getCollapsedOutputCategories(filter).length;

const isSameIdSet = (prevIds: string[], currentIds: string[]) =>
  prevIds.length === currentIds.length &&
  prevIds.every((id) => currentIds.includes(id));

export const isSameOutputCategoryFilter = (
  prev: OutputCategoryFilterState,
  current: OutputCategoryFilterState,
) => {
  if (
    prev.outputCategoryFilterMode === 'exclude' &&
    current.outputCategoryFilterMode === 'exclude'
  ) {
    return isSameIdSet(
      prev.excludeOutputDocumentCategories,
      current.excludeOutputDocumentCategories,
    );
  }

  if (
    prev.outputCategoryFilterMode === 'include' &&
    current.outputCategoryFilterMode === 'include'
  ) {
    return isSameIdSet(
      prev.outputDocumentCategories,
      current.outputDocumentCategories,
    );
  }

  return false;
};
